import type { ReactNode } from 'react'
import { ThemedColors, gemVar } from '@opallabs/gem'
import { pluralize } from '@opallabs/lambda'
import * as styles from './styles'

export type ParentGroup = { id: string; name: string }

export type BreakdownTooltipArgs = {
  breakdown?: Record<string, number>
  colorFor?: (groupId: string) => string
  heading: string
  isContentMode: boolean
  nameFor?: (groupId: string) => string
  parentOf?: (groupId: string) => ParentGroup | null
  totalCount: number
}

/** The color of a group nobody has given one to. */
export const FALLBACK_SEGMENT_COLOR = gemVar(ThemedColors.IconSecondary)

const NO_PARENT = '__no_parent__'

// Rank by count desc with id as a stable tiebreaker so the stack doesn't
// reshuffle across renders. Used for both the flat and grouped breakdowns.
const compareByCountDescIdAsc = (
  [idA, countA]: [string, number],
  [idB, countB]: [string, number]
): number => {
  if (countB !== countA) return countB - countA
  return idA.localeCompare(idB)
}

// Rank groups by count desc so the dominant slice sits at the base of the bar.
export const sortedBreakdownEntries = (
  breakdown: Record<string, number>
): Array<[string, number]> =>
  Object.entries(breakdown).sort(compareByCountDescIdAsc)

// Two-level sort for parent-grouped breakdowns (Account → Service):
// parents ordered by total count desc, then leaves within each parent
// by count desc. Drives the tooltip's grouped layout.
const groupedSortedEntries = (
  breakdown: Record<string, number>,
  parentOf: (groupId: string) => ParentGroup | null
): Array<{
  parent: ParentGroup | null
  entries: Array<[string, number]>
  total: number
}> => {
  const buckets = new Map<
    string,
    {
      parent: ParentGroup | null
      entries: Array<[string, number]>
      total: number
    }
  >()
  Object.entries(breakdown).forEach(([groupId, count]) => {
    const parent = parentOf(groupId)
    const key = parent?.id ?? NO_PARENT
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = { parent, entries: [], total: 0 }
      buckets.set(key, bucket)
    }
    bucket.entries.push([groupId, count])
    bucket.total += count
  })
  buckets.forEach((bucket) => {
    bucket.entries.sort(compareByCountDescIdAsc)
  })
  return Array.from(buckets.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total
    return (a.parent?.name ?? '').localeCompare(b.parent?.name ?? '')
  })
}

const percent = (count: number, total: number): number =>
  total > 0 ? Math.round((count / total) * 100) : 0

const ChipDot: React.FC<{ color: string }> = ({ color }) => (
  <span aria-hidden css={styles.dot} style={{ backgroundColor: color }} />
)

const TooltipRow: React.FC<{
  color?: string
  label: string
  count: number
  percentLabel: string
  inset?: boolean
  bold?: boolean
}> = ({ color, label, count, percentLabel, inset, bold }) => (
  <div css={[styles.row, inset && styles.rowInset, bold && styles.rowTotal]}>
    {color ? <ChipDot color={color} /> : <span css={styles.dotSpacer} />}
    <span css={styles.rowLabel}>{label}</span>
    <span>
      {count} ({percentLabel}%)
    </span>
  </div>
)

// Pure renderer for the day/week tooltip body — caller owns the
// `<Tooltip>` wrapper so each variant can set its own side/heading.
//
// When `parentOf` is supplied AND any group has a parent (Account sort),
// the tooltip groups leaves under colored parent headers — the parent
// row carries the chip + total, leaves render plain underneath. For
// flat sorts (Channel/LabelSet/Time) every leaf gets its own chip row.
export const renderBreakdownTooltipContent = ({
  breakdown,
  colorFor,
  heading,
  isContentMode,
  nameFor,
  parentOf,
  totalCount,
}: BreakdownTooltipArgs): ReactNode => {
  if (totalCount === 0) return `${heading} • No scheduled content`
  const hasBreakdown =
    breakdown != null && Object.keys(breakdown).length > 0 && totalCount > 0
  if (!hasBreakdown) {
    // "content" is the user-facing term and is uncountable (no plural).
    const countLabel = isContentMode
      ? `${totalCount} content`
      : `${totalCount} ${pluralize('moment', totalCount)}`
    return `${heading} · ${countLabel}`
  }

  const grouped = parentOf
    ? groupedSortedEntries(breakdown!, parentOf)
    : [
        {
          parent: null as ParentGroup | null,
          entries: sortedBreakdownEntries(breakdown!),
          total: Object.values(breakdown!).reduce((a, b) => a + b, 0),
        },
      ]
  const segmentTotal = grouped.reduce((sum, g) => sum + g.total, 0)
  const hasParents = grouped.some(({ parent }) => parent != null)

  return (
    <div css={styles.body}>
      <div css={styles.heading}>
        {heading} · {totalCount} total
      </div>
      {grouped.map(({ parent, entries, total }) => (
        <div key={parent?.id ?? NO_PARENT}>
          {hasParents && parent && (
            <TooltipRow
              color={colorFor?.(parent.id) ?? FALLBACK_SEGMENT_COLOR}
              label={parent.name}
              count={total}
              percentLabel={String(percent(total, segmentTotal))}
              bold
            />
          )}
          {entries.map(([groupId, count]) => {
            const insetLeaf = hasParents && parent != null
            return (
              <TooltipRow
                key={groupId}
                color={
                  insetLeaf
                    ? undefined
                    : (colorFor?.(groupId) ?? FALLBACK_SEGMENT_COLOR)
                }
                label={nameFor?.(groupId) ?? 'Unknown'}
                count={count}
                percentLabel={String(percent(count, segmentTotal))}
                inset={insetLeaf}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}
